import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { PrefetchLink } from '@sorare/routing';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelL } from '@sorare/core/src/atoms/typography';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_ONBOARDING_SERIES_SELECT_COMPETITION,
  FOOTBALL_ONBOARDING_TUTORIAL_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage';
import { useUnlockProMode } from '@sorare/core/src/hooks/useUnlockProMode';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { useSkipOnboardingMutation } from '@sorare/gameplay/src/hooks/onboarding/useSkipOnboardingMutation';

import { MarketGuidanceLayout } from '#src/components/onboarding/MarketGuidanceLayout/index.tsx';
import { WinItems } from '#src/components/onboarding/WinItems/index.tsx';
import { ConversionCredit } from '#src/pages/onboarding_/tutorial/ConversionCredit/index.tsx';

const Root = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  flex: 1;
  width: 100%;

  @media ${tabletAndAbove} {
    width: auto;
  }
`;

const TutorialWinPage = () => {
  const navigate = useNavigate();
  const { sport, generateSportContextPath } = useSportContext();
  const { unlockProMode } = useUnlockProMode();
  const [onboardingPro] = useLocalStorage('ONBOARDING_PRO', undefined);
  const [skipOnboarding] = useSkipOnboardingMutation();

  const onContinue = async () => {
    await Promise.all([
      unlockProMode({
        variables: {
          sport,
          input: { sport, interactionContext: 'onboardingPro' },
        },
      }),
      skipOnboarding({
        variables: {
          input: { sport, interactionContext: 'onboardingPro' },
          sport,
        },
      }),
    ]);
    navigate(
      `${generateSportContextPath(ANY_SPORT_MARKET_SHOP_AUCTIONS)}?${SEARCH_PARAMS.COMPETITION_ELIGIBILITY}=${onboardingPro}`
    );
  };

  return (
    <MarketGuidanceLayout
      progress={4}
      center
      backUrl={FOOTBALL_ONBOARDING_TUTORIAL_PLAY}
      cta={
        <Vertical gap={0}>
          <ConversionCredit />
          <OnboardingButton size="large" color="secondary" onClick={onContinue}>
            <FormattedMessage {...glossary.continue} />
          </OnboardingButton>
        </Vertical>
      }
      ctaBackgroundColor="#0c0524"
    >
      <PrefetchLink
        to={generateSportContextPath(
          ANY_SPORT_ONBOARDING_SERIES_SELECT_COMPETITION
        )}
      />
      <Root>
        <Vertical gap={1.5} center>
          <FormattedMessage
            id="TutorialWinPage.title"
            defaultMessage="<em>Win</em><label>amazing rewards each Game Week</label>"
            values={{
              em: chunks => <BrandL>{chunks}</BrandL>,
              label: chunks => <LabelL>{chunks}</LabelL>,
            }}
          />
        </Vertical>
        <WinItems />
      </Root>
    </MarketGuidanceLayout>
  );
};
export default TutorialWinPage;
